import type { Guest } from "@/types/Guest";

/**
 * The guest list as a TREE, not a list.
 *
 * On the party-grouped list (`groupBy === "mainGuest"`) the provider's
 * `guests` array holds the party HEADS, and each head carries its companions
 * as full rows under `companions[]`. Every id-keyed lookup that only walks the
 * top level therefore misses a companion — and misses it silently, because a
 * `find` that returns `undefined` reads as "no such guest" rather than "wrong
 * level". That is how an inline edit of a companion was a no-op: the staging
 * guard did not find the row, so nothing was staged, and nothing was said.
 *
 * These three helpers are the ONE place that knows the tree has two levels.
 * They are also correct on the flat list: there a row's `companions[]` are
 * projection chips (`_id, firstName, lastName`) rather than rows, and touching
 * a chip is harmless — a patched chip is a fresher chip.
 *
 * Identity is preserved on purpose. `mapGuestTree` returns the SAME head
 * object when neither the head nor any of its companions changed, and the
 * SAME array when nothing changed at all, so the memoised rows do not re-render
 * and a functional `setGuests` can bail out.
 */

/** Heads and their nested companions, in render order (head, its companions, next head). */
export function flattenGuestTree(guests: Guest[]): Guest[] {
  const out: Guest[] = [];
  for (const head of guests) {
    out.push(head);
    if (head.companions?.length) out.push(...head.companions);
  }
  return out;
}

/** Top level first, then nested companions. `undefined` when the id is on neither. */
export function findGuestById(guests: Guest[], guestId: string): Guest | undefined {
  for (const head of guests) {
    if (head.guestId === guestId) return head;
  }
  for (const head of guests) {
    const nested = head.companions?.find((c) => c.guestId === guestId);
    if (nested) return nested;
  }
  return undefined;
}

/**
 * Apply `fn` to every top-level guest AND every nested companion.
 *
 * `fn` sees each person once, on its own, and returns either the same object
 * (no change) or a new one. A head whose own row and companions all came back
 * unchanged is returned as the same object; a list where nothing changed is
 * returned as the same array.
 */
export function mapGuestTree(guests: Guest[], fn: (guest: Guest) => Guest): Guest[] {
  let listChanged = false;
  const next = guests.map((head) => {
    const mappedHead = fn(head);
    const companions = mappedHead.companions;
    if (!companions?.length) {
      if (mappedHead !== head) listChanged = true;
      return mappedHead;
    }
    let companionsChanged = false;
    const mappedCompanions = companions.map((companion) => {
      const mapped = fn(companion);
      if (mapped !== companion) companionsChanged = true;
      return mapped;
    });
    if (!companionsChanged) {
      if (mappedHead !== head) listChanged = true;
      return mappedHead;
    }
    listChanged = true;
    return { ...mappedHead, companions: mappedCompanions };
  });
  return listChanged ? next : guests;
}
